<template>
  <OptimizationBaseDialog
    v-model="visibility"
    :optimization="props.optimization"
    :person="props.person">
    <SmTable
      v-if="accounts"
      ref="table"
      key-field="_id"
      :data="accounts"
      :columns="columns">
      <template #header>
        <p
          class="sm-table-header-cell px-3 font-bold"
          :class="{ 'text-red': validation == false }">
          {{ i18n.t('chooseSelectionMessage') }}:
        </p>
      </template>

      <template #account="{ row }">
        <div v-if="applications" class="flex items-center gap-3">
          <ApplicationIcon
            v-if="applications[row.software_id]"
            :application-name="
              applications[row.software_id]?.display_name ?? ''
            "
            class="shrink-0"
            :size="20" />
          <p>{{ applications[row.software_id]?.display_name }}</p>
        </div>
      </template>

      <template #action="{ row }">
        <SmSelect
          v-model:selection="changes[row._id]"
          :options="options"
          :label="i18n.t('chooseOptions')"
          trigger-style="plain"
          dropdown-min-width="150px"
          class="cursor-pointer"
          @change="
            (option) => {
              if (!option) return
              setChange(row._id, option)
            }
          " />
      </template>
    </SmTable>

    <!-- Footer -->
    <template #footer>
      <SmButton
        v-if="props.optimization.hidden && !props.optimization.deprecated"
        outline
        @click="emit('unhide', props.optimization._id)">
        {{ i18n.t('unhideOptimization') }}
      </SmButton>
      <SmButton
        v-else-if="!props.optimization.hidden && !props.optimization.deprecated"
        outline
        @click="emit('hide', props.optimization._id)">
        {{ i18n.t('views.optimizations.table.actions.hideOptimization') }}
      </SmButton>
      <SmButton @click="executeChanges()">
        {{ i18n.t('execute') }}
      </SmButton>
    </template>
  </OptimizationBaseDialog>
</template>

<script lang="ts" setup>
  import { useI18n } from 'vue-i18n'

  import {
    EnrichedOptimizations,
    OptimizationStore,
  } from '@/stores/optimization/optimization.store'
  import {
    Account,
    DataSource,
    DataSourcesService,
    LicenceOut,
    SoftwareOut,
  } from '@/client'
  import { AccountStore } from '@/stores/account/account.store'
  import { Column } from '@/components/sm/SmTable.types'
  import { ComponentExposed } from 'vue-component-type-helpers'
  import SmTable from '@/components/sm/SmTable.vue'
  import { ApplicationStore } from '@/stores/application/application.store'
  import { Option } from '@/components/sm/SmSelect.vue'
  import { sendToast } from '@/components/sm/SmNotification'

  const i18n = useI18n()

  const props = defineProps({
    optimization: {
      type: Object as PropType<EnrichedOptimizations>,
      required: true,
    },
    person: {
      type: Object as PropType<Account>,
      required: true,
    },
  })

  const visibility = defineModel({
    type: Boolean,
    required: true,
  })

  const emit = defineEmits(['hide', 'unhide'])

  // ---------------------------------------------------------------------------
  // Load Data
  // ---------------------------------------------------------------------------

  onMounted(async () => {
    reloadConnectedDataSources()
    await loadAccounts()
    await loadApplications()
  })

  const dataSource = ref<null | DataSource>(null)
  const accounts = ref<LicenceOut[] | null>(null)
  const applications = reactive<Record<string, SoftwareOut>>({})

  function reloadConnectedDataSources() {
    if (!props.optimization.references.data_source_id) return

    DataSourcesService.getDataSourceApiV1DataSourceDataSourcesDataSourceIdGet({
      dataSourceId: props.optimization.references.data_source_id,
    }).then((res: DataSource) => {
      dataSource.value = res
    })
  }

  async function loadAccounts() {
    await AccountStore.getAccounts({
      email: props.person.email,
    }).then((res) => {
      if (res) {
        accounts.value = res
      }
    })
  }

  async function loadApplications() {
    const ids = accounts.value?.map((a) => a.software_id) || []
    const uniqueIds = ids.filter((id) => !(id in applications))

    if (uniqueIds.length) {
      const res = await ApplicationStore.getApplications<SoftwareOut>({
        ids: uniqueIds,
      })
      res.forEach((app) => {
        applications[app._id] = app
      })
    }
  }

  // ----------------------------------------------------------------------
  // Selection
  // ----------------------------------------------------------------------

  const options = ref<Option<string>[]>([
    {
      label: i18n.t('general.keep'),
      value: 'keep',
    },
    {
      label: i18n.t('general.remove'),
      value: 'remove',
    },
  ])

  const changes = ref<Record<string, Option<string>[]>>({})

  function setChange(licenseId: string, option: Option<string>[]) {
    changes.value[licenseId] = option
  }

  // ----------------------------------------------------------------------
  // Table
  // ----------------------------------------------------------------------

  const table = ref<ComponentExposed<typeof SmTable<LicenceOut>>>()

  const columns = computed<Column<LicenceOut>[]>(() => [
    {
      key: 'account',
      label: i18n.t('account'),
      sortable: false,
      width: 1,
      loader: true,
    },
    {
      key: 'action',
      label: '',
      width: 'auto',
      sortable: false,
    },
  ])

  // ----------------------------------------------------------------------
  // Execute changes
  // ----------------------------------------------------------------------

  const validation = ref<boolean>()

  function executeChanges() {
    validation.value = validate()

    if (validation.value) {
      for (const id in changes.value) {
        if (changes.value[id][0].value === 'remove') {
          AccountStore.deleteAccount(id)
        }
      }

      sendToast(
        i18n.t(
          'notifications.applicationsDetailView.licences.saveLicencesSuccess.message'
        ),
        undefined,
        'success'
      )

      OptimizationStore.refreshOptimizationCacheFromApi({}, false, false)
      visibility.value = false
    } else {
      sendToast(
        i18n.t(
          'notifications.applicationsDetailView.licences.saveLicencesFailed.message'
        ),
        undefined,
        'error'
      )
      console.error(`Accounts could not be saved`)
    }
  }

  function validate(): boolean {
    if (accounts.value === null) return false
    return Object.keys(changes.value).length === accounts.value?.length
  }
</script>
